
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Check, 
  ShieldCheck, 
  Clock, 
  PiggyBank, 
  Info, 
  Home, 
  CreditCard, 
  Car, 
  Anchor, 
  Wrench, 
  Wallet 
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const EnglishPage = () => {
  const isMobile = useIsMobile();
  
  return (
    <>
      <Helmet>
        <title>Lainasampo.fi | Compare loans and save money</title>
        <meta name="description" content="Lainasampo.fi helps you find the most affordable loan. Compare mortgage, consumer credit or consolidation loan easily with one application." />
        <meta name="language" content="en" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-10 md:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="text-3xl md:text-5xl font-bold text-blue-700 mb-4 md:mb-6">
                  Compare loans and save money
                </h1>
                <p className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8">
                  Get the best loan offers from multiple banks with a single application.
                </p>
                <Link to="/en/apply">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-6 md:px-8 py-3 md:py-6 text-base md:text-lg rounded-md shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                    Compare loan offers now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* About Finnish Loans Section */}
        <section className="py-10 md:py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6 md:mb-8">
                Understanding Loans in Finland
              </h2>
              
              <div className="bg-blue-50 p-4 md:p-6 rounded-xl mb-8">
                <div className="flex items-start mb-4">
                  <Info className="h-5 md:h-6 w-5 md:w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-sm md:text-base text-gray-700">
                    The Finnish loan market offers competitive interest rates and transparent terms. Whether you're looking for a 
                    consumer loan, mortgage, or business financing, Finland's well-regulated banking system provides security and reliability.
                  </p>
                </div>
                
                <h3 className="text-lg md:text-xl font-medium text-gray-800 mb-3">Key features of Finnish loans:</h3>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">Fixed interest rates for most consumer loans, providing payment stability</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">No requirement for physical bank visits - fully digital application process</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">Thorough credit checks through Suomen Asiakastieto to ensure responsible lending</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">Flexible repayment options including early repayment possibilities</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Loan Types Section */}
        <section className="py-10 md:py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8 md:mb-10">
                Loan Types Available
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-3">
                    <CreditCard className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-lg font-medium text-gray-800">Consumer Credit</h3>
                  </div>
                  <p className="text-sm text-gray-600">Financing for everyday purchases and needs.</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-3">
                    <Home className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-lg font-medium text-gray-800">Mortgage</h3>
                  </div>
                  <p className="text-sm text-gray-600">Affordable financing for home purchases.</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-3">
                    <Car className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-lg font-medium text-gray-800">Car Loan</h3>
                  </div>
                  <p className="text-sm text-gray-600">Finance your dream vehicle with competitive rates.</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-3">
                    <Anchor className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-lg font-medium text-gray-800">Boat Loan</h3>
                  </div>
                  <p className="text-sm text-gray-600">Specialized financing for boat purchases and maintenance.</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-3">
                    <Wrench className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-lg font-medium text-gray-800">Renovation Loan</h3>
                  </div>
                  <p className="text-sm text-gray-600">Finance home improvements and renovations.</p>
                </div>
                
                <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-3">
                    <Wallet className="h-8 w-8 text-blue-600 mr-3" />
                    <h3 className="text-lg font-medium text-gray-800">Debt Consolidation</h3>
                  </div>
                  <p className="text-sm text-gray-600">Combine multiple loans to simplify payments and save money.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-10 md:py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
                Why choose Lainasampo.fi?
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-5 rounded-xl">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Free comparison</h3>
                  <p className="text-sm text-gray-600">
                    Compare loan offers from multiple banks at once, completely free of charge.
                  </p>
                </div>
                
                <div className="bg-blue-50 p-5 rounded-xl">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Quick response</h3>
                  <p className="text-sm text-gray-600">
                    Get loan offers within 24 hours. No paperwork or bank visits required.
                  </p>
                </div>
                
                <div className="bg-blue-50 p-5 rounded-xl">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <PiggyBank className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Save money</h3>
                  <p className="text-sm text-gray-600">
                    Find the best interest rates and save thousands of euros over the loan period.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 md:py-16 bg-blue-600 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready to find the best loan?
              </h2>
              <p className="text-lg mb-6 text-blue-100">
                Fill out a single application and we'll find you the best offers.
              </p>
              <Link to="/en/apply">
                <Button size="lg" className="bg-white hover:bg-gray-100 text-blue-600 px-6 py-3 text-base rounded-md shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                  Start my application
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default EnglishPage;
